import { Div, Flex, Text } from '@sendoutcards/quantum-design-ui'
import React, { useRef } from 'react'
import {
  SelectableUpsaleOptions,
  SelectableUpsaleOptionsType,
} from './components/SelectableUpsaleOptions'
import {
  UpsaleFeatureList,
  UpsaleListProps,
} from './components/UpsaleFeatureList'
import { useEffect, useSelector, useState } from 'src/hooks'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { UpsaleHeader } from './components/UpsaleHeader'
import { UpsaleFooter } from './components/UpsaleFooter'

type ActionType = {
  title: string
  onClick: () => void
}

type PlanType = {
  title: string
  description: string
  planOptions: SelectableUpsaleOptionsType
  planFeatureList: UpsaleListProps
}

type FullPageUpsaleProps = {
  plan: PlanType
  isOpen: boolean
  upsaleImage: string
  primaryAction: ActionType
  secondaryAction?: ActionType
  backgroundGradient?: string
  onShowTerms?: () => void
  onViewAll: () => void
  onClose: () => void
}

export const FullPageUpsale = (props: FullPageUpsaleProps) => {
  const {
    plan,
    isOpen,
    upsaleImage,
    primaryAction,
    secondaryAction,
    backgroundGradient = 'radial-gradient(circle at 80% 0%, #9859fe 0%, transparent 45%),linear-gradient(180deg, #9859fe -30.21%, #f075d4 25.82%, white 100%)',
    onShowTerms,
    onViewAll,
    onClose,
  } = props
  const isMobile = useSelector(state => state.window.width <= 760)
  const baseRatioRef = useRef(window.devicePixelRatio)
  const [isZoomed, setIsZoomed] = useState(false)
  const [isHovering, setIsHovering] = useState(false)

  const fontFamily = 'Montserrat, sans-serif'

  const imgSize = isMobile ? '300px' : '400px'

  const wrapperStyles = {
    width: '100%',
    height: '100%',
    background: backgroundGradient,
    zIndex: 1000,
    position: 'fixed',
    overflowY: 'auto',
    top: '0',
    left: '0',
    justifyContent: 'center',
    alignItems: isMobile ? 'flex-start' : 'center',
  } as const

  const actionSharedStyles = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '16px',
    boxShadow: '0px 4px 32px 0px rgba(0, 0, 0, 0.08)',
    fontSize: '16px',
    fontFamily: fontFamily,
    fontWeight: 600,
    cursor: 'pointer',
  }

  const actionsStyles = {
    ...actionSharedStyles,
    padding: '8px 48px',
    background: '#000',
    color: '#fff',
    flexDirection: 'row' as const,
    columnGap: '5px',
    transform: isHovering ? 'scale(1.03)' : 'initial',
    transition: '.3s ease-in-out',
  }

  const zoomedClosestyles = {
    ...actionSharedStyles,
    padding: '8px 18px',
    background: '#fff',
    color: '#000',
  }

  useEffect(() => {
    const checkZoom = () => {
      const currentRatio = window.devicePixelRatio
      setIsZoomed(currentRatio !== baseRatioRef.current)
    }

    checkZoom()
    window.addEventListener('resize', checkZoom)
    return () => window.removeEventListener('resize', checkZoom)
  }, [])

  return (
    <>
      {isOpen && (
        <Flex style={{ ...wrapperStyles }}>
          <div
            style={{
              width: '100%',
              maxWidth: '700px',
              position: 'fixed',
              top: '0',
              transform: 'translate(0px, 5vh)',
            }}
          >
            <UpsaleHeader
              onClose={onClose}
              inset={{ horizontal: isMobile ? 'x3' : '' }}
            />
          </div>
          <Flex
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            style={{ paddingTop: '60px' }}
          >
            <Flex
              justifyContent="center"
              alignItems="center"
              flexWrap={isMobile ? 'wrap' : 'nowrap'}
            >
              <Flex flexDirection="column" alignItems="center">
                <Div
                  style={{
                    width: imgSize,
                    height: imgSize,
                    backgroundImage: `url(${upsaleImage})`,
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'contain',
                  }}
                />
                <Flex flexDirection="column" alignItems="center" rowGap="x2">
                  <Text
                    type="body"
                    content={plan.title}
                    style={{
                      color: '#212121',
                      fontFamily: fontFamily,
                      fontSize: '28px',
                      fontWeight: 700,
                    }}
                  />
                  <Div maxWidth="230px">
                    <Text
                      type="body"
                      alignment="center"
                      lineHeight={1.5}
                      content={plan.description}
                      style={{
                        fontFamily: fontFamily,
                        color: '#404040',
                        fontWeight: 600,
                        fontSize: '14px',
                      }}
                    />
                  </Div>
                  <div>
                    <Flex flexDirection="row" columnGap="x_75">
                      <Div
                        onClick={primaryAction.onClick}
                        onHoverStart={() => setIsHovering(true)}
                        onHoverEnd={() => setIsHovering(false)}
                        style={{ ...actionsStyles }}
                      >
                        <Icon name="premium_crown" color="#FECD39" size={14} />
                        {primaryAction.title}
                      </Div>
                      {isZoomed && (
                        <Div onClick={onClose} style={{ ...zoomedClosestyles }}>
                          x
                        </Div>
                      )}
                    </Flex>
                    {secondaryAction && (
                      <>
                        <Text
                          type="caption"
                          content="Or"
                          alignment="center"
                          weight="bold"
                        />
                        <Div
                          onClick={secondaryAction.onClick}
                          onHoverStart={() => setIsHovering(true)}
                          onHoverEnd={() => setIsHovering(false)}
                          style={{
                            ...actionsStyles,
                            backgroundColor: '#FFF',
                            color: 'rgb(75, 85, 99)',
                          }}
                        >
                          {secondaryAction.title}
                        </Div>
                      </>
                    )}
                  </div>
                </Flex>
              </Flex>
              <Flex
                flexDirection="column"
                rowGap="x1"
                justifyContent="center"
                alignItems="center"
                inset={isMobile ? { top: 'x3' } : {}}
              >
                <SelectableUpsaleOptions {...plan.planOptions} />
                <UpsaleFeatureList {...plan.planFeatureList} />
              </Flex>
            </Flex>
            <UpsaleFooter
              onShowTerms={onShowTerms}
              onViewAll={onViewAll}
              inset={{ top: '60px', bottom: '100px' }}
            />
          </Flex>
        </Flex>
      )}
    </>
  )
}
